import { apiDownloadBlob, apiGet, apiPost, apiPostBinary } from "@/services/api/client";

export interface ManagedAccount {
  id: string;
  displayName: string;
  username: string;
  tenant: { id: string; name: string };
  status: "active" | "disabled";
  hasManagedPassword: boolean;
  isSuperAdmin: boolean;
  createdAt: string;
}

export interface AccountList {
  items: ManagedAccount[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ImportPreview {
  fileSha256: string;
  valid: boolean;
  rowCount: number;
  issues: Array<{ row: number; field?: string; message: string }>;
  limits: { compressedBytes: number; uncompressedBytes: number; zipEntries: number };
}

export interface ImportResult {
  fileSha256: string;
  created: number;
  failed: number;
  notProcessed: number;
  unconfirmed: number;
  rows: Array<{
    row: number;
    status: "created" | "failed" | "not_processed" | "unconfirmed";
    account?: ManagedAccount;
    message?: string;
  }>;
}

export const accountRepository = {
  list(status: "active" | "disabled" | "all" = "active", page = 1, pageSize = 20) {
    return apiGet<AccountList>(
      `/admin/accounts?page=${page}&pageSize=${pageSize}&status=${status}`,
    );
  },
  create(input: { displayName: string; phone: string }) {
    return apiPost<ManagedAccount>("/admin/accounts", input);
  },
  reveal(id: string) {
    return apiPost<{ username: string; password: string }>(
      `/admin/accounts/${encodeURIComponent(id)}/reveal-credential`,
    );
  },
  disable(id: string) {
    return apiPost<{ id: string; status: "disabled" }>(
      `/admin/accounts/${encodeURIComponent(id)}/disable`,
      { confirmed: true },
    );
  },
  previewImport(file: File) {
    return apiPostBinary<ImportPreview>("/admin/accounts/import?apply=false", file);
  },
  applyImport(file: File, fileSha256: string) {
    return apiPostBinary<ImportResult>(
      `/admin/accounts/import?apply=true&fileSha256=${encodeURIComponent(fileSha256)}`,
      file,
    );
  },
  async downloadTemplate() {
    const blob = await apiDownloadBlob("/admin/accounts/template");
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "childplus-accounts-template.xlsx";
    anchor.click();
    URL.revokeObjectURL(url);
  },
};
